import {
  autoUpdate,
  shift,
  useFloating,
  type ReferenceType,
} from '@floating-ui/react';
import {noop} from 'lodash';
import {useLayoutEffect, useMemo} from 'react';
import {useInView} from 'react-intersection-observer';

import {useFloatingActionButtonMargin} from '@/hooks/useFloatingActionButtonMargin';

type UseFloatingMapButtonProps = {
  onPosition: (params: {element?: null | HTMLElement; margin: number}) => void;
};

type UseFloatingMapButtonReturnValue = {
  buttonRef: (node: HTMLElement | null) => void;
  positionRef: (node: ReferenceType | null) => void;
  visibilityRef: (node?: Element | null | undefined) => void;
  buttonStyles: {
    position: 'fixed';
    top: 'auto';
    left: `${number}px`;
    bottom: `${number}px`;
  };
  canBeShown: boolean;
};

export const useFloatingMapButton = ({
  onPosition = noop,
}: UseFloatingMapButtonProps): UseFloatingMapButtonReturnValue => {
  const margin = useFloatingActionButtonMargin();

  const {refs, x, y, elements} = useFloating({
    placement: 'bottom',
    middleware: [
      shift({
        crossAxis: true,
        padding: margin,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const [visibilityRef, inView] = useInView({
    rootMargin: '-100% 0px 0px 0px',
    fallbackInView: false,
  });

  // we only care about the horizontal axis as calculated by floating-ui,
  // vertical axis is always at the bottom so it causes no scroll "jank".
  const floatingStyles: UseFloatingMapButtonReturnValue['buttonStyles'] =
    useMemo(
      () => ({
        position: 'fixed',
        top: 'auto',
        left: `${x}px`,
        bottom: `${margin}px`,
      }),
      [x, margin]
    );

  // callback for collision detection as x and y position changes
  useLayoutEffect(() => {
    onPosition({element: elements.floating, margin});
  }, [x, y, elements.floating, margin, onPosition]);

  return {
    buttonRef: refs.setFloating,
    positionRef: refs.setReference,
    visibilityRef,
    buttonStyles: floatingStyles,
    canBeShown: inView,
  };
};
